import type { ChartGroup } from '@integratio/core';
import { formatPercent } from '../../lib/format';
import { StakeholderGroupList } from './StakeholderGroupList';

interface AxisConfig {
  label: string;
  minus?: string;
  plus?: string;
}

interface Props {
  groups: ChartGroup[];
  rows: number;
  cols: number;
  total: number;
  axisX: AxisConfig;
  axisY: AxisConfig;
  listLimit?: number;
  minCellHeight?: string;
}

function Cell({ group, total, listLimit, minHeight }: { group: ChartGroup | undefined; total: number; listLimit: number; minHeight: string }): JSX.Element {
  if (!group) {
    return <div className="rounded-2xl border border-dashed border-brand-line/70" style={{ minHeight }} />;
  }

  const empty = group.count === 0;

  return (
    <div
      className={[
        'avoid-break relative overflow-hidden rounded-2xl border bg-white p-4 pl-5',
        empty ? 'border-brand-line/80 bg-brand-mist/40' : 'border-brand-line shadow-card',
      ].join(' ')}
      style={{ minHeight }}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1.5"
        style={{ backgroundColor: empty ? '#E5E5EA' : group.color }}
      />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p
            className="text-[13px] font-bold uppercase tracking-wide"
            style={{ color: empty ? '#8E8C99' : group.color }}
          >
            {group.name}
          </p>
          {group.subtitle && (
            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-slate/80">
              {group.subtitle}
            </p>
          )}
        </div>
        <div className="text-right">
          <p
            className="text-[26px] font-semibold leading-none tabular-nums"
            style={{ color: empty ? '#8E8C99' : group.color }}
          >
            {formatPercent(group.percentage)}
          </p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-brand-slate">
            {group.count} de {total}
          </p>
        </div>
      </div>

      <div className="mt-3 border-t border-brand-line/70 pt-3">
        <StakeholderGroupList stakeholders={group.stakeholders} emptyText={group.emptyText} limit={listLimit} compact />
      </div>
    </div>
  );
}

/** Matriz de quadrantes com eixos rotulados, usada por Contexto do Ambiente e Postura e Comportamento. */
export function MatrixChart({
  groups,
  rows,
  cols,
  total,
  axisX,
  axisY,
  listLimit = 6,
  minCellHeight = '150px',
}: Props): JSX.Element {
  const positioned = new Map<string, ChartGroup>();
  const extras: ChartGroup[] = [];
  for (const group of groups) {
    if (group.matrix) positioned.set(`${group.matrix.row}-${group.matrix.col}`, group);
    else extras.push(group);
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        {/* Eixo vertical */}
        <div className="flex w-9 flex-col items-center justify-between py-1">
          <span className="text-lg font-bold leading-none text-brand-slate">{axisY.plus ?? '+'}</span>
          <span
            className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-brand text-brand-slate"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {axisY.label}
          </span>
          <span className="text-lg font-bold leading-none text-brand-slate">{axisY.minus ?? '–'}</span>
        </div>

        <div className="flex-1">
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {Array.from({ length: rows }).flatMap((_, row) =>
              Array.from({ length: cols }).map((__, col) => (
                <Cell
                  key={`${row}-${col}`}
                  group={positioned.get(`${row}-${col}`)}
                  total={total}
                  listLimit={listLimit}
                  minHeight={minCellHeight}
                />
              )),
            )}
          </div>

          {/* Eixo horizontal */}
          <div className="mt-3 flex items-center gap-3">
            <span className="text-lg font-bold leading-none text-brand-slate">{axisX.minus ?? '–'}</span>
            <span className="h-px flex-1 bg-brand-line" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-brand text-brand-slate">{axisX.label}</span>
            <span className="h-px flex-1 bg-brand-line" aria-hidden />
            <span className="text-lg font-bold leading-none text-brand-slate">{axisX.plus ?? '+'}</span>
          </div>
        </div>
      </div>

      {extras.length > 0 && (
        <div className="rounded-2xl border border-brand-line bg-brand-mist/60 p-4">
          <p className="eyebrow">Classificações fora do dicionário padrão</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {extras.map((group) => (
              <div key={group.key} className="rounded-xl border border-brand-line bg-white p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-graphite">{group.name}</p>
                  <p className="text-sm font-semibold tabular-nums text-brand-graphite">
                    {formatPercent(group.percentage)}
                  </p>
                </div>
                <p className="mt-1 text-[10px] uppercase tracking-wider text-brand-slate">
                  {group.count} de {total}
                </p>
                <div className="mt-2">
                  <StakeholderGroupList
                    stakeholders={group.stakeholders}
                    emptyText={group.emptyText}
                    limit={listLimit}
                    compact
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
